import type { Dispatch } from 'react';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';
import { QUOTE_VIDEO_URL } from './quoteVideoUrl';
import { SectionCompanyName } from './sections/SectionCompanyName';
import { SectionFurnitureType } from './sections/SectionFurnitureType';
import { SectionComposition } from './sections/SectionComposition';
import { SectionCost } from './sections/SectionCost';
import { SectionConditions } from './sections/SectionConditions';
import { SectionExecutorContacts } from './sections/SectionExecutorContacts';
import { SectionComments } from './sections/SectionComments';
import { BottomActionButtons } from './sections/BottomActionButtons';
import type { QuoteAction } from './quoteReducer';
import type { QuoteState } from './types';

/**
 * Block 12 PR-D-A — Quote page presenter (ADR-0005).
 *
 * Pure function: рендерит layout shell — Header + doc-header card + 7 sections
 * + BottomBar. Каждая section получает только то, что ей нужно из
 * (state, dispatch).
 *
 * **Doc-header card (Phase D10 Bug #1):** sub-Header card с title документа
 * («Коммерческое предложение») + date генерации («Сформировано 18 мая 2026 г.»).
 * Mirrors prototype `docs/prototypes/screen-2.12.10-cp.html` lines 123-128.
 * Использует `<h2>` (top app Header — `<h1>`, избегаем a11y h1-duplicate
 * violation). Date рендерится через `new Intl.DateTimeFormat('ru-RU', {long})`
 * на каждый render — фиксацию createdAt в state.documentCreatedAt трекает
 * **B-129** в BACKLOG (current render-time approximation acceptable для МVP).
 *
 * **No top-level DisclaimerBanner (Phase D10 Bug #2):** КП — финальный документ
 * для клиента, не интерактивный расчёт. Caption «Погрешность ±10%» рендерится
 * только под SectionCost (per PRD §10.12.10:402). Top-level banner вводил в
 * заблуждение, что весь экран — оценочный, а не готовое КП.
 *
 * Reference: prototype `docs/prototypes/screen-2.12.10-cp.html` lines 105-365.
 * Sequence: Header → DocHeaderCard → projectId info (optional) →
 * SectionFurnitureType → SectionComposition → SectionCost → SectionConditions
 * → SectionExecutorContacts → SectionComments → BottomActionButtons →
 * BottomBar.
 */

const DATE_FORMATTER = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
export interface QuotePageViewProps {
  state: QuoteState;
  dispatch: Dispatch<QuoteAction>;
  /** Source projectId из URL query (для traceability; Phase D5 driving seeding). */
  projectId: string | null;
  /** projectId либо 'standalone' — для save/clear квот-драфта. */
  draftKey: string;
  /** PR-C1 (ADR-0035) — путь к существующему Договору по этому КП, либо null. */
  forwardContractPath?: string | null;
}

export function QuotePageView({
  state,
  dispatch,
  projectId,
  draftKey,
  forwardContractPath,
}: QuotePageViewProps) {
  const generatedAt = DATE_FORMATTER.format(new Date());
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Коммерческое предложение" />
      <div className="flex-1 overflow-y-auto pb-6 pt-3 flex flex-col gap-4">
        <div className="mx-3 mt-3">
          <VideoButton videoUrl={QUOTE_VIDEO_URL} />
        </div>
        <section
          data-testid="quote-doc-header"
          className="bg-card border-2 border-accent rounded-2xl shadow-sm px-4 py-3 mx-3 text-center"
          aria-labelledby="quote-doc-header-title"
        >
          <h2 id="quote-doc-header-title" className="font-display text-ink text-h-logo m-0">
            Коммерческое предложение
          </h2>
          <p className="text-xs text-ink/60 m-0 mt-1" data-testid="quote-doc-header-date">
            Сформировано {generatedAt}
          </p>
        </section>

        {/* DEV-only trace для отладки seed-flow; hidden в production build (Vite tree-shakes). */}
        {import.meta.env.DEV && projectId ? (
          <p className="text-muted text-sm leading-snug px-3" data-testid="quote-page-project-id">
            Источник расчёта: <code className="font-mono text-xs">{projectId}</code>
          </p>
        ) : null}

        <SectionCompanyName state={state} dispatch={dispatch} />
        <SectionFurnitureType state={state} dispatch={dispatch} />
        <SectionComposition state={state} dispatch={dispatch} />
        <SectionCost state={state} dispatch={dispatch} />
        <SectionConditions state={state} dispatch={dispatch} />
        <SectionExecutorContacts state={state} dispatch={dispatch} />
        <SectionComments state={state} dispatch={dispatch} />
        <BottomActionButtons
          state={state}
          draftKey={draftKey}
          forwardContractPath={forwardContractPath}
        />
      </div>
      <BottomBar variant="default" />
    </main>
  );
}
